/**
 * @swoft/gtd-domain - Unified GTD Domain
 * 
 * Rich Domain-Driven Design implementation of David Allen's Getting Things Done methodology
 * Following Eric Evans Strategic Design patterns with proper bounded context isolation
 * 
 * Core GTD Principles Implemented:
 * 1. Ubiquitous Capture - capture everything that crosses your mind
 * 2. Clarification - what is it? is it actionable?
 * 3. Organization - organize by context and priority
 * 4. Reflection - review and update regularly
 * 5. Engagement - choose actions confidently
 */

export { InboxItemApplicationService } from './bounded-contexts/inbox-management';

// ============================================
// INBOX MANAGEMENT BOUNDED CONTEXT
// ============================================
export * from './bounded-contexts/inbox-management';

// ============================================
// PROJECT MANAGEMENT BOUNDED CONTEXT  
// ============================================
export * from './bounded-contexts/project-management';

// ============================================
// CROSS-CONTEXT APPLICATION SERVICES
// ============================================
export { WeeklyReviewApplicationService } from './bounded-contexts';

// ============================================
// TASK MANAGEMENT BOUNDED CONTEXT
// ============================================
// Temporarily disabled due to @swoft/core dependency issues - will fix in next release
// export { ITaskRepository } from './bounded-contexts/task-management/ports/ITaskRepository';
// export { TaskMongoRepository } from './bounded-contexts/task-management/infrastructure/repositories/TaskMongoRepository';
// export { TaskAssigned } from './bounded-contexts/task-management/domain/events/TaskAssigned';
// export { TaskId } from './bounded-contexts/task-management/domain/value-objects/TaskId';
// export { AssignmentInfo } from './bounded-contexts/task-management/domain/value-objects/AssignmentInfo';

// ============================================
// AI TOOLS BOUNDED CONTEXT
// ============================================
export * from './bounded-contexts/ai-tools';

// ============================================
// QUERY SERVICES (CQRS Read-Side) - Removed query-infrastructure BC
// ============================================
// Query functionality is now properly organized within each bounded context
// following Eric Evans DDD principles. No shared "query-infrastructure" BC needed.

// Factory function for creating query services (Published Language interface)
export { createGTDQueryService } from './bounded-contexts/inbox-management/infrastructure/factories/createGTDQueryService';
export type { GTDQueryService, PersonLookupService } from './bounded-contexts/inbox-management/infrastructure/factories/createGTDQueryService';

// ============================================
// SIMPLIFIED SERVICE FACTORY (Party Manager Pattern)
// ============================================
// Simple factory approach - no Inversify complexity
export { GTDServiceFactory } from './infrastructure/factory/GTDServiceFactory';

// Re-export Published Language contracts for backward compatibility
export type GTDInboxItemDTO = import('./integration/contracts').InboxItemSummary;

export interface GTDNextActionDTO {
  id: string;
  description: string;
  context?: string;
  energyRequired?: string;
  createdAt: Date;
  estimatedMinutes?: number;
  isCompleted?: boolean;
  projectId?: string;
  assignedTo?: string;
  status?: 'available' | 'assigned' | 'completed';
}

export interface GTDQueryFilters {
  status?: string;
  limit?: number;
  offset?: number;
  context?: string;
  energyRequired?: string;
  assignedTo?: string;
}

// ============================================
// API CONTRACTS BOUNDED CONTEXT (Type-safe HTTP Interfaces)
// ============================================
// Note: Temporarily commented out due to external dependencies
// export * from './bounded-contexts/api-contracts';

// ============================================
// INTEGRATION CONTRACTS (Published Language)
// ============================================
// Strategic DDD - Published Language for downstream contexts
export * from './integration';

// ============================================
// SEMANTIC NAVIGATION
// ============================================
// GTD-specific semantic navigation and workflow intelligence
export * from './navigation';

// ============================================
// SHARED/CROSS-CUTTING CONCERNS
// ============================================
// Interface Layer - Errors
export {
  GTDFeatureError,
  GTDValidationError,
  GTDDomainIntegrationError
} from './interface/errors/GTDFeatureError';

// MongoDB Collections Configuration
export * from './config/collections';

// Domain Seeder
export * from './infrastructure/GtdDomainSeeder';

// ============================================
// DEPENDENCY INJECTION MODULE
// ============================================
// Modern ContainerModule Pattern (Recommended - matches party-manager and DDD)
export { createGTDDomainModule, GTD_DOMAIN_SYMBOLS } from './infrastructure/di/GTDDomainContainerModule';

// ============================================
// DEPENDENCY INJECTION TYPES
// ============================================  
export { TYPES } from './types/DITypes';

// ============================================
// DOMAIN RULES (for DDD Health Analysis)
// ============================================
export { createGTDDomainRules } from './validation/GTDDomainRules';
  